import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Text } from 'wix-ui-tpa/cssVars';
import { DetailsSectionViewModel } from '../../../../service-page-view-model/details-section-view-model/detailsSectionViewModel';
import {
  SHOW_DISCOUNT_EXPERIMENT,
  SUBSCRIPTION_PRICING_EXPERIMENT,
} from '../../constants';
import { useAddVisibilityClass } from '../../hooks/useAddVisibilityClass';
import { useSectionVisibilityCheck } from '../../hooks/useSectionVisibilityCheck';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import settingsParams from '../../settingsParams';
import { AlignmentOptions, ISection, SectionTypes } from '../../types';
import Section from '../Section';
import { useGlobalAlignment } from '../useGlobalAlignment';
import { classes, st } from './Details.st.css';
import { DiscountName } from './DiscountName';
import { SubscriptionInfo } from './SubscriptionInfo';

type DetailsProps = {
  viewModel?: DetailsSectionViewModel;
  section: ISection;
  className?: string;
};

const DetailsComponent: React.FC<DetailsProps> = ({ viewModel, className }) => {
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const globalAlignment = useGlobalAlignment();
  const { shouldSectionBookButtonBeVisible } = useSectionVisibilityCheck();
  const { addVisibilityClass } = useAddVisibilityClass();
  const { shouldBeVisible } = useVisibilityCheck();
  const { experiments } = useExperiments();
  const isDiscountEnabled = experiments.enabled(SHOW_DISCOUNT_EXPERIMENT);
  const isSubscriptionEnabled = experiments.enabled(
    SUBSCRIPTION_PRICING_EXPERIMENT,
  );

  const buttonAlignment =
    settings.get(settingsParams.detailsBoxAlignment) ===
    AlignmentOptions.STRETCH
      ? settings.get(settingsParams.detailsButtonAlignment)
      : settings.get(settingsParams.detailsBoxAlignment);
  const textAlignment =
    (settings.get(settingsParams.detailsBoxAlignment) !==
    AlignmentOptions.STRETCH
      ? settings.get(settingsParams.detailsBoxAlignment)
      : settings.get(settingsParams.detailsTextAlignment)) || globalAlignment;
  const boxWidth =
    settings.get(settingsParams.detailsBoxAlignment) ===
    AlignmentOptions.STRETCH
      ? '100%'
      : undefined;
  const withBookButton =
    shouldSectionBookButtonBeVisible(SectionTypes.DETAILS) &&
    viewModel?.isBookable &&
    viewModel?.isFullyBooked !== true;
  const showDuration =
    shouldBeVisible('detailsDurationVisibility') && viewModel?.duration;
  const showPrice =
    shouldBeVisible('detailsPriceVisibility') && viewModel?.price;
  const showLocation =
    shouldBeVisible('detailsLocationVisibility') && viewModel?.location;
  const showLocations =
    shouldBeVisible('detailsLocationVisibility') &&
    viewModel?.locations?.length;
  const showAvailability =
    shouldBeVisible('courseAvailability') && !!viewModel?.numberOfSpotsLeft;

  const content: JSX.Element[] = [];
  if (showDuration) {
    content.push(
      <Text
        key="details-duration"
        className={addVisibilityClass(
          classes.box,
          classes.durationVisibility,
          'detailsDurationVisibility',
        )}
        data-hook="details-duration"
        tagName="span"
      >
        <span aria-hidden>{viewModel?.duration}</span>
        <span
          className={classes.srOnly}
          data-hook="details-duration-aria-label"
        >
          {viewModel?.durationAria}
        </span>
      </Text>,
    );
  }
  if (showPrice) {
    content.push(
      <span
        key="details-price"
        className={addVisibilityClass(
          '',
          classes.priceVisibility,
          'detailsPriceVisibility',
        )}
      >
        <div className={classes.srOnly} data-hook="sr-only-details-price">
          {viewModel?.ariaPrice}
        </div>
        <div aria-hidden={true} data-hook="aria-hidden-details-price">
          {isDiscountEnabled && viewModel.priceAfterDiscount ? (
            <div className={st(classes.box, classes.pricesRow)}>
              <Text
                className={classes.priceBeforeDiscount}
                data-hook="details-price-before-discount"
              >
                {viewModel.price}
              </Text>
              <Text data-hook="details-price">
                {viewModel.priceAfterDiscount}
              </Text>
            </div>
          ) : (
            <Text
              className={classes.box}
              data-hook="details-price"
              tagName="div"
            >
              {viewModel?.price}
            </Text>
          )}
        </div>
      </span>,
    );
  }
  if (showLocation) {
    content.push(
      <Text
        key="details-location"
        className={addVisibilityClass(
          classes.box,
          classes.locationVisibility,
          'detailsLocationVisibility',
        )}
        data-hook="details-location"
        tagName="span"
      >
        {viewModel?.location}
      </Text>,
    );
  }

  if (showLocations) {
    content.push(
      <Text
        key="details-locations"
        className={classes.box}
        data-hook="details-locations"
        tagName="span"
      >
        {viewModel?.locations?.map((location, index) => {
          return (
            <>
              {location}
              {index !== Number(viewModel?.locations?.length) - 1 && (
                <span className={classes.locationDivider}>|</span>
              )}
            </>
          );
        })}
      </Text>,
    );
  }

  const availability = showAvailability ? (
    <Text
      key="details-course-availability"
      className={classes.courseAvailability}
      data-hook="details-course-availability"
      tagName="p"
    >
      {viewModel?.numberOfSpotsLeft}
    </Text>
  ) : null;
  return withBookButton || !!content.length ? (
    <Section
      sectionType={SectionTypes.DETAILS}
      withBookButton={withBookButton}
      buttonAlignment={buttonAlignment}
      childrenAlignment={settings.get(settingsParams.detailsBoxAlignment)}
      availability={availability}
      className={classes.section}
    >
      <div
        style={{ width: boxWidth }}
        className={st(
          classes.root,
          {
            textAlignment,
            isMobile,
            isRTL,
          },
          className,
        )}
        data-hook="details-wrapper"
      >
        {content}
      </div>
      {isSubscriptionEnabled && showPrice ? (
        <SubscriptionInfo text={viewModel?.subscriptionInfoText} />
      ) : null}
      <DiscountName
        discountName={viewModel?.discountName}
        showCalculatedAtCheckout={
          viewModel?.isVariedPrice ||
          viewModel?.hasAddOns ||
          !viewModel?.priceAfterDiscount
        }
      />
    </Section>
  ) : null;
};

export default DetailsComponent;
